/**
 * UsersHeader — shared header used by both UsersListPage
 * (`/users`) and InvitationsListPage (`/users/invitations`).
 *
 * Unifies the Users section under a single mental model: one h1,
 * one count chip, one Invite button, and a two-tab navigation
 * (Members ↔ Pending invitations) so admins don't need to switch
 * pages to manage who's on their team.
 *
 * The two pages render their own table + pagination + state
 * panels INSIDE this shared header so the rest of the layout
 * stays identical regardless of tab. NavLink drives navigation +
 * `aria-current` for free.
 *
 * Permission gating: the "Pending invitations" tab is only
 * rendered if the actor has `identity.invitations.read`. The
 * "+ Invite user" button is independently gated by
 * `identity.invitations.create`.
 */

import * as React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Button } from '@sparta/ui-kit';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { RequirePermission } from '../../../shared/auth/RequirePermission.js';

export interface UsersHeaderProps {
  readonly activeTab: 'members' | 'invitations';
  readonly count: string;
  readonly description?: string;
}

const TAB_BASE_CLASSES =
  'inline-flex items-center min-h-touch px-4 text-sm font-semibold no-underline border-b-2 transition-colors focus-visible:outline-2 focus-visible:outline-[var(--brand-500)] focus-visible:outline-offset-2';

function tabClasses(isActive: boolean): string {
  if (isActive) {
    return `${TAB_BASE_CLASSES} text-[var(--fg-primary)] border-[var(--brand-500)]`;
  }
  return `${TAB_BASE_CLASSES} text-[var(--fg-secondary)] border-transparent hover:text-[var(--fg-primary)]`;
}

const DEFAULT_DESCRIPTIONS: Record<UsersHeaderProps['activeTab'], string> = {
  members:
    'People with access to this organization. Search, filter, and sort across the full member directory.',
  invitations:
    "Invitations awaiting acceptance. Resend to refresh the link (the old one is automatically revoked) or revoke to cancel the invitation entirely.",
};

export function UsersHeader({
  activeTab,
  count,
  description,
}: UsersHeaderProps): React.ReactElement {
  const orgState = useCurrentOrg();
  const navigate = useNavigate();
  const orgName = orgState?.membership.organizationName ?? '';

  return (
    <header className="flex flex-col gap-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--fg-tertiary)]">
            Organization
          </span>
          <h1
            id="users-page-title"
            className="text-2xl font-semibold tracking-tight text-[var(--fg-primary)]"
          >
            Users
          </h1>
        </div>
        <RequirePermission slug="identity.invitations.create">
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate('/users/invite')}
          >
            + Invite user
          </Button>
        </RequirePermission>
      </div>
      <p className="max-w-2xl text-sm text-[var(--fg-secondary)]">
        {description ?? DEFAULT_DESCRIPTIONS[activeTab]}
      </p>
      <p className="text-sm text-[var(--fg-tertiary)]">
        {count}
        {orgName.length > 0 ? ` · ${orgName}` : ''}
      </p>
      <nav
        role="tablist"
        aria-label="Users sections"
        className="flex border-b border-[var(--border-subtle)]"
      >
        <NavLink
          to="/users"
          end
          role="tab"
          aria-selected={activeTab === 'members'}
          className={tabClasses(activeTab === 'members')}
        >
          Members
        </NavLink>
        <RequirePermission slug="identity.invitations.read">
          <NavLink
            to="/users/invitations"
            end
            role="tab"
            aria-selected={activeTab === 'invitations'}
            className={tabClasses(activeTab === 'invitations')}
          >
            Pending invitations
          </NavLink>
        </RequirePermission>
      </nav>
    </header>
  );
}
